// ============================================================
// ProtectedRoute.jsx
// Auth guard - checks Supabase session before rendering any
// protected page. Redirects to the correct login when the
// session is gone or the account no longer has access.
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme, fonts } from '../styles/theme';
import { getSession } from '../utils/auth';
import { getUserRole } from '../utils/investorApi';

const ProtectedRoute = ({ children, requiredRole }) => {
  const [status, setStatus] = useState('checking');
  const navigate = useNavigate();
  const location = useLocation();

  const redirectToLogin = useCallback((reason = null) => {
    const isInvestorRoute = requiredRole === 'investor' || String(location.pathname || '').startsWith('/portal');
    if (isInvestorRoute) {
      const suffix = reason ? `?notice=${encodeURIComponent(reason)}` : '';
      navigate(`/investor-login${suffix}`, { replace: true });
      return;
    }
    navigate('/login', { replace: true });
  }, [location.pathname, navigate, requiredRole]);

  useEffect(() => {
    const check = async () => {
      try {
        const session = await getSession();
        if (!session) {
          redirectToLogin('request-access');
          return;
        }

        if (requiredRole) {
          const role = await getUserRole();
          if (!role) {
            redirectToLogin('request-access');
            return;
          }
          if (role !== requiredRole) {
            navigate(role === 'investor' ? '/portal' : '/trade-center', { replace: true });
            return;
          }
        }

        setStatus('allowed');
      } catch {
        redirectToLogin('request-access');
      }
    };

    check();
  }, [redirectToLogin, navigate, requiredRole]);

  if (status === 'checking') {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: theme.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.textDim,
          fontFamily: fonts.main,
          fontSize: 13,
        }}
      >
        Checking session...
      </div>
    );
  }

  return status === 'allowed' ? children : null;
};

export default ProtectedRoute;
